'use client';

// Components
import { AtHeader } from '@/components/atoms/at-header/at-header';
import { AtIcon } from '@/components/atoms/at-icon/at-icon';
import MapSvg from '@/components/atoms/at-icon/icons/Map';
import { AtSetLocationAction } from '@/components/atoms/at-set-location-action/at-set-location-action';
import { AtClickable } from '@/components/atoms/at-clickable/at-clickable';
import { AtArrowLink } from '@/components/atoms/at-arrow-link/at-arrow-link';
import { AtDivider } from '@/components/atoms/at-divider/at-divider';
import { AtText } from '@/components/atoms/at-text/at-text';
import { AtTag } from '@/components/atoms/at-tag/at-tag';
import { AddressGenerator } from '@/components/helpers/formatters/AddressBuilder';

// Types
import { ETextVariants } from '@/components/atoms/at-text/at-text.types';
import { ETagVariants } from '@/components/atoms/at-tag/at-tag.types';
import {
  EClickableSize,
  EClickableTarget,
  EClickableTypes,
  EClickableVariants,
} from '@/components/atoms/at-clickable/at-clickable.types';
import { EIcons } from '@/components/atoms/at-icon/icons';
import {
  EDividerDirectionalVariants,
  EDividerVariants,
  EDividerColors,
} from '@/components/atoms/at-divider/at-divider.types';
import { ESetLocationActionVariants } from '@/components/atoms/at-set-location-action/at-set-location-action.types';
import {
  ESearchLocationFormVariants,
  IFormattedLocationItem,
  ILocationItem,
} from './ml-search-location-form.types';
import { IDesignStudio } from '@/components/organisms/or-design-studio-block/or-design-studio-block.types';
import { IStudioHit } from '@/lib/search/types';
import { THours } from '@/components/templates/tm-design-center-detail/tm-design-center-details.types';

// Context / Hooks / Utils
import { useLocation } from '@/context/locationContext';
import {
  ESetLocationType,
  useSetLocationModal,
} from '@/context/setLocationContext';
import { LoginModalContext } from '@/context/loginModalContext';
import { locationModalMicrocopy } from './constants';
import {
  sendSelectLocationOnMapGTMEvent,
  sendZeroResultsGTMEvent,
} from '@/components/helpers/analytics/gtm';

// Third-party / React
import classNames from 'classnames';
import Fuse, { FuseResult } from 'fuse.js';
import {
  ChangeEvent,
  Dispatch,
  SetStateAction,
  useEffect,
  useMemo,
  useState,
  useRef,
  useContext,
} from 'react';
import { sendGTMEvent } from '@next/third-parties/google';
import { debounce } from 'lodash';
import { accountUrl } from '@/constants';

/*
 * OLD location search using Fuse fuzzy search
 */
export const MlSearchLocationFormFuse = ({
  title,
  onElementClick,
  studioId,
  locationData,
  selectedText,
  redirect = null,
  type = ESetLocationType.DEFAULT,
  variant = ESearchLocationFormVariants.HERO,
}: {
  title: string;
  onElementClick: (sId: number | null, selectedText: string | null) => void;
  studioId?: number | null;
  locationData: ILocationItem[];
  studioData: IStudioHit[];
  selectedText: string | null;
  redirect?: string | null;
  variant?: ESearchLocationFormVariants;
  type?: ESetLocationType;
}) => {
  const isModalVariant = variant === ESearchLocationFormVariants.MODAL;

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchResults, setSearchResults] = useState<ILocationItem[]>([]);
  const [resultsCount, setResultsCount] = useState<number>(0);
  const resultsAnnouncementRef = useRef<HTMLDivElement>(null); // Ref for live region

  const [isFocused, setIsFocused] = useState<boolean>(false);
  const [hasClickedResult, setHasClickedResult] = useState<boolean>(false);
  const [isClient, setIsClient] = useState(false);

  const { buildLoginUrl } = useContext(LoginModalContext);
  useEffect(() => {
    setIsClient(true);
  }, []);

  const microcopy = useMemo(() => {
    return locationModalMicrocopy[type];
  }, [type]);
  const { currentBuildStudio } = useLocation();

  const fuse = useMemo(() => {
    return new Fuse(locationData as ILocationItem[], {
      keys: ['name', 'state_name', 'state_abbrev', 'county_name', 'zip'],
      threshold: 0.1,
      includeScore: true,
    });
  }, [locationData]);

  // Debounced search handler
  const debouncedSearch = useRef(
    debounce((query: string) => {
      if (!query.trim()) {
        setSearchResults([]);
        setResultsCount(0);
        return;
      }

      const results: FuseResult<ILocationItem>[] = fuse.search(query);

      const matchedItems: ILocationItem[] = results
        .sort((a, b) => (a.score || 0) - (b.score || 0))
        .map((result) => result.item);

      setSearchResults(matchedItems);
      setResultsCount(matchedItems.length);

      if (matchedItems.length === 0) {
        sendZeroResultsGTMEvent(query, 'Location');
      }
    }, 350),
  ).current;

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
    };
  }, [debouncedSearch]);

  const handleSearch = (event: ChangeEvent<HTMLInputElement>) => {
    const query = event.target.value.toLowerCase();
    setSearchQuery(query);
    debouncedSearch(query);
  };

  // Update live region on resultsCount change
  useEffect(() => {
    if (resultsAnnouncementRef.current && searchQuery.trim() !== '') {
      const message =
        resultsCount > 0
          ? `${resultsCount} results found.`
          : 'No results found.';
      resultsAnnouncementRef.current.textContent = message;
    }
  }, [resultsCount, searchQuery]);

  const uniqueResults = useMemo(() => {
    const seenDisplayTexts = new Set();
    const seenCountyIds = new Set();
    const results: IFormattedLocationItem[] = [];

    const queryLower = searchQuery.toLowerCase();
    const isStateAbbrevSearch = locationData.some(
      (loc) => loc.state_abbrev.toLowerCase() === queryLower,
    );
    const isStateNameSearch = locationData.some(
      (loc) => loc.state_name.toLowerCase() === queryLower,
    );

    searchResults.forEach((item) => {
      const cityMatch = item.name.toLowerCase().includes(queryLower);
      const countyMatch = item.county_name.toLowerCase().includes(queryLower);
      const stateAbbrevMatch = item.state_abbrev
        .toLowerCase()
        .includes(queryLower);
      const stateNameMatch = item.state_name.toLowerCase().includes(queryLower);
      const zipMatch = item.zip.some((z) => z.includes(searchQuery));

      // 1. City match — always show city
      if (cityMatch) {
        const displayText = `${item.name}, ${item.state_abbrev}`;
        if (!seenDisplayTexts.has(displayText)) {
          seenDisplayTexts.add(displayText);
          results.push({ ...item, displayText });
        }
        return;
      }

      // 2. County or ZIP match — show county
      if ((countyMatch || zipMatch) && !seenCountyIds.has(item.county_id)) {
        const displayText = `${item.county_name} County, ${item.state_abbrev}`;
        seenCountyIds.add(item.county_id);
        seenDisplayTexts.add(displayText);
        results.push({ ...item, displayText });
        return;
      }

      // 3. State abbreviation or name match — prefer county
      if (
        (isStateAbbrevSearch && stateAbbrevMatch) ||
        (isStateNameSearch && stateNameMatch)
      ) {
        const displayText = !seenCountyIds.has(item.county_id)
          ? `${item.county_name} County, ${item.state_abbrev}`
          : `${item.name}, ${item.state_abbrev}`;

        if (!seenDisplayTexts.has(displayText)) {
          seenDisplayTexts.add(displayText);
          if (displayText.includes('County')) {
            seenCountyIds.add(item.county_id);
          }
          results.push({ ...item, displayText });
        }
        return;
      }
    });

    return results.sort((a, b) => a.displayText.localeCompare(b.displayText));
  }, [searchResults, searchQuery, locationData]);

  const handleSetLocationAction = () => {
    onElementClick(null, null);
    setSearchQuery('');
  };

  const { handleClose } = useSetLocationModal();

  const showMenu =
    (uniqueResults.length > 0 && searchQuery.length > 0) ||
    (uniqueResults.length <= 0 && searchQuery.length > 0);

  const showDesignCenterInfo = !hasClickedResult && studioId && isModalVariant;

  const redirectLink = useMemo(() => {
    if (type === ESetLocationType.ACCOUNT) {
      // We expect that StudioId will exist given we are now gating the account button
      return `${accountUrl}?StudioId=${studioId}`;
    }
    if (redirect) {
      return (
        buildLoginUrl({ StudioId: String(studioId) }, redirect || '') ?? ''
      );
    }
    return '';
  }, [buildLoginUrl, redirect, studioId, type]);

  return (
    <div
      className={classNames({
        '!rounded-b-0 !rounded-t-18 lg:!rounded-t-30':
          showMenu && !isModalVariant,
        'rounded-18 lg:rounded-30': !showMenu && !isModalVariant,
        'flex flex-col items-center justify-center': studioId && isModalVariant,
        'relative bg-white': true,
        'lg:pb-5.5 lg:pt-0': isModalVariant,
        'p-6 lg:px-8 lg:pb-5.5 lg:pt-8': !isModalVariant,
      })}
    >
      {/* Live region for screen reader announcements */}
      <div
        ref={resultsAnnouncementRef}
        aria-live="polite"
        className="sr-only"
        role="status"
      />
      {isModalVariant && !showDesignCenterInfo && (
        <>
          <AtHeader element="h4" className="text-balance pb-6 text-center">
            {microcopy?.title}
          </AtHeader>
        </>
      )}

      {!showDesignCenterInfo && isClient && (
        <AtHeader
          variant="h6"
          element="p"
          className={classNames(
            { 'pb-4.75': studioId, 'md:mx-24': isModalVariant },
            'pb-6',
          )}
        >
          {studioId && isModalVariant
            ? microcopy?.selectedLocationText
            : studioId
              ? 'Welcome! Find more customizable plans in:'
              : microcopy?.subtitle}
        </AtHeader>
      )}

      {!studioId && (
        <div className={classNames({ 'md:mx-24': isModalVariant }, 'z-1')}>
          <form className="flex flex-row border-b-2 border-b-skin-stroke-primary pb-2 lg:pb-4.5">
            <input
              type="text"
              placeholder="Search by city, county or ZIP"
              value={searchQuery}
              onChange={handleSearch}
              onFocus={() => setIsFocused(true)}
              onBlur={() => setIsFocused(false)}
              className="h5-md w-full placeholder-search focus:outline-none"
            ></input>
            <span className="text-24">
              <AtIcon
                icon={EIcons.SEARCH}
                color="var(--color-ui-icon-secondary)"
              />
            </span>
          </form>
          <div className="">
            {uniqueResults.length > 0 && searchQuery.length > 0 && (
              <ResultsMenu
                results={uniqueResults}
                onElementClick={onElementClick}
                selectedText={selectedText}
                setHasClickedResult={
                  isModalVariant ? setHasClickedResult : undefined
                }
                variant={variant}
              />
            )}

            {uniqueResults.length <= 0 && searchQuery.length > 0 && (
              <NoResultsMenu variant={variant} />
            )}
          </div>
        </div>
      )}

      {studioId && (
        <div
          className={classNames({
            'flex w-full flex-col items-center justify-center bg-white px-6 pb-5':
              isModalVariant,
            'absolute left-0 z-10 w-full rounded-b-18 bg-white px-6 pb-5 shadow-locationMenu lg:rounded-b-30':
              !isModalVariant,
          })}
        >
          {hasClickedResult && isModalVariant && (
            <>
              <AtSetLocationAction
                hasLocation
                onClick={handleSetLocationAction}
                variant={ESetLocationActionVariants.HERO}
                className="mb-6"
              >
                {selectedText}
              </AtSetLocationAction>
              <AtClickable
                variant={EClickableVariants.PRIMARY}
                outline
                href={redirectLink}
                onClick={() => {
                  handleClose();
                  setHasClickedResult(false);
                }}
                interactLocation="ml-search-location-form"
              >
                {microcopy?.buttonText}
              </AtClickable>
            </>
          )}

          {showDesignCenterInfo && (
            <DesignCenterInfo
              studioId={studioId}
              handleSetLocationAction={handleSetLocationAction}
              selectedText={selectedText}
              currentBuildStudio={currentBuildStudio}
              studioHours={currentBuildStudio?.hours || []}
              studioPageUrl={currentBuildStudio?.pageUrl || ''}
            />
          )}

          {!isModalVariant && (
            <>
              <AtSetLocationAction
                hasLocation
                onClick={handleSetLocationAction}
                variant={ESetLocationActionVariants.HERO}
                className="mb-6"
              >
                {selectedText}
              </AtSetLocationAction>
              <div className="flex justify-end">
                <AtArrowLink
                  label="Continue My Search"
                  href="/house-plans"
                  aria_label={`View search results in ${selectedText}`}
                />
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

interface IDesignCenterInfoProps {
  studioId: number | null;
  handleSetLocationAction: () => void;
  selectedText: string | null;
  currentBuildStudio: null | IDesignStudio;
  studioHours: THours[];
  studioPageUrl: string;
}

const DesignCenterInfo = ({
  studioId,
  handleSetLocationAction,
  selectedText,
  currentBuildStudio,
  studioHours,
  studioPageUrl,
}: IDesignCenterInfoProps) => (
  <div className="flex w-full flex-col md:flex-row">
    <div className="flex flex-col items-center justify-center md:w-1/2">
      <AtHeader
        variant="h6"
        element="p"
        className={classNames({ 'pb-4.75': studioId }, 'pb-6')}
      >
        You&apos;ve set your build location:
      </AtHeader>
      <div>
        <AtSetLocationAction
          hasLocation
          onClick={handleSetLocationAction}
          variant={ESetLocationActionVariants.HERO}
          className="mb-6"
        >
          {selectedText}
        </AtSetLocationAction>
      </div>
    </div>
    <AtDivider
      direction={EDividerDirectionalVariants.VERTICAL}
      intent={EDividerVariants.THICKER}
      color={EDividerColors.GREEN}
      decorative={true}
      customClasses="h-auto mx-6 hidden md:block"
    />
    <AtDivider
      direction={EDividerDirectionalVariants.HORIZONTAL}
      intent={EDividerVariants.THICKER}
      color={EDividerColors.GREEN}
      decorative={true}
      customClasses="w-auto mb-6 h-0.03 block md:hidden"
    />
    <div className="custom-scrollbar landscape:lg:max-h-auto w-full px-8.9 md:w-1/2 md:px-15 landscape:max-h-[calc(50dvh_-_100px)] landscape:overflow-y-auto landscape:md:max-h-[calc(100dvh_-_150px)] landscape:lg:overflow-visible">
      <AtHeader
        variant="h6"
        element="p"
        className="pb-6 text-center text-skin-primary md:text-left"
      >
        Your nearby design studio:
      </AtHeader>
      <div className="flex flex-col justify-start">
        <AtText className="font-semibold">
          {`${currentBuildStudio?.CsName || currentBuildStudio?.Name}`} Design
          Studio
        </AtText>
        {!currentBuildStudio?.IsVirtual && (
          <AddressGenerator studioData={currentBuildStudio} />
        )}

        {studioHours.length > 0 && (
          <>
            <AtText
              size="sm"
              type={ETextVariants.LABEL}
              className="mb-4 mt-6 inline-block text-13 font-semibold uppercase tracking-0.64 md:text-11 lg:text-base"
            >
              Hours
            </AtText>
            <span>
              {studioHours.map(({ dates }, idx) => {
                const { hours, days } = dates;
                return (
                  <span key={`${hours}-${days}-${idx}`}>
                    <AtText>
                      <span className="font-semibold">{days}:</span> {hours}
                    </AtText>
                  </span>
                );
              })}
            </span>
          </>
        )}
        {studioPageUrl && (
          <AtClickable
            href={studioPageUrl}
            variant={EClickableVariants.PRIMARY}
            className="mt-6 w-fit"
            size={EClickableSize.SM}
            interactLocation="ml-search-location-form"
            shouldSendGTMEvent={false}
            onClick={() => {
              sendSelectLocationOnMapGTMEvent(
                'View Design Studio',
                studioPageUrl,
              );
            }}
          >
            View Design Studio
          </AtClickable>
        )}
      </div>
    </div>
  </div>
);

const NoResultsMenu = ({
  variant,
}: {
  variant: ESearchLocationFormVariants;
}) => (
  <div
    className={classNames('bg-white px-6 pb-5 pt-5', {
      'absolute left-0 rounded-b-18 shadow-locationMenu lg:rounded-b-30':
        variant === ESearchLocationFormVariants.HERO,
    })}
  >
    <AtText
      size="sm"
      type={ETextVariants.LABEL}
      className="mb-4 inline-block text-11 font-semibold uppercase tracking-0.64 lg:text-base"
    >
      No Results
    </AtText>
    <div className="flex flex-row justify-between">
      <span className="hidden lg:block lg:w-fit">
        <MapSvg />
      </span>
      <span className="block lg:hidden">
        <MapSvg width={100} height={58} />
      </span>
      <div className="ml-3 flex flex-col gap-3">
        <AtText size="sm">
          We&apos;re sorry, we don&apos;t yet serve this area. Try another
          search or explore a map of our service areas.
        </AtText>
        <AtClickable
          href="/where-we-build"
          className="hidden w-fit lg:block"
          variant={EClickableVariants.PRIMARY}
          size={EClickableSize.SM}
          type={EClickableTypes.LINK}
          target={EClickableTarget.SELF}
          interactLocation="ml-search-location-form"
        >
          View where we build map
        </AtClickable>
      </div>
    </div>
    <div className="flex w-full items-center justify-center pt-4.75 lg:hidden">
      <AtClickable
        href="/where-we-build"
        className="block w-fit lg:hidden"
        variant={EClickableVariants.PRIMARY}
        size={EClickableSize.SM}
        type={EClickableTypes.LINK}
        target={EClickableTarget.SELF}
        interactLocation="ml-search-location-form"
      >
        View where we build map
      </AtClickable>
    </div>
  </div>
);

const ResultsMenu = ({
  results,
  onElementClick,
  setHasClickedResult,
  variant,
}: {
  results: IFormattedLocationItem[];
  onElementClick: (id: number, selectedText: string | null) => void;
  selectedText: string | null;
  setHasClickedResult?: Dispatch<SetStateAction<boolean>>;
  variant: ESearchLocationFormVariants;
}) => {
  const handleClick = (id: number | undefined, selectedText: string | null) => {
    if (id) {
      onElementClick(id, selectedText);
      setHasClickedResult?.(true);
    }
  };
  return (
    <div
      className={classNames(
        'z-[8] w-full rounded-b-18 bg-white pb-5 pt-5 lg:rounded-b-30',
        {
          'absolute left-0 px-6 shadow-locationMenu':
            variant === ESearchLocationFormVariants.HERO,
        },
      )}
    >
      <AtText
        size="sm"
        type={ETextVariants.LABEL}
        className="mb-4 inline-block text-11 font-semibold uppercase tracking-0.64 lg:text-base"
      >
        Results
      </AtText>
      <ul
        aria-live="polite"
        className="custom-scrollbar flex max-h-230 flex-col gap-y-3 overflow-y-auto lg:max-h-250 landscape:md:max-h-60 landscape:lg:max-h-250"
      >
        {results.map((item, index) => {
          return (
            <li
              className="w-fit"
              aria-posinset={index}
              aria-setsize={results.length}
              key={`${item.name}-${item.zip}-${item.county_id}`}
            >
              <AtTag
                variant={ETagVariants.LOCATION}
                size={EClickableSize.SM}
                onClick={() => {
                  sendGTMEvent({
                    event: 'build_location_setting',
                    value: item.displayText,
                    is_search_by_nearest: false,
                  });
                  handleClick(item.studio_id, item.displayText);
                }}
              >
                {item.displayText}
              </AtTag>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
